import * as React from 'react'
import dayjs, { Dayjs } from 'dayjs'
import Button from '@mui/material/Button'
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { UseDateFieldProps } from '@mui/x-date-pickers/DateField'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import {
  BaseSingleInputFieldProps,
  DateValidationError,
  FieldSection
} from '@mui/x-date-pickers/models'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay'
import { Badge } from '@mui/material'
import { useProtocolsList } from '@services/protocols/protocols.hook'
import { protocolsDate, leaseDate, getProtocolFetchRange } from '@services/date/date.models'
import { useAtom, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'

import 'dayjs/locale/pl'

interface ButtonFieldProps
  extends UseDateFieldProps<Dayjs, false>,
    BaseSingleInputFieldProps<Dayjs | null, Dayjs, FieldSection, false, DateValidationError> {
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>
}

const Day: React.FC<PickersDayProps<Dayjs> & { protocols?: any[] }> = ({
  protocols = [],
  day,
  ...props
}) => {
  const hasEvents = !!protocols
    .filter(({ leaseStartDate, effectiveLeaseEnd }) => !!leaseStartDate || !!effectiveLeaseEnd)
    .filter(
      ({ leaseStartDate, effectiveLeaseEnd }) =>
        day.isSame(leaseStartDate, 'day') || day.isSame(effectiveLeaseEnd, 'day')
    ).length

  if (props.outsideCurrentMonth || !hasEvents) {
    return <PickersDay {...props} day={day} />
  }

  return (
    <Badge color='error' key={day.toString()} overlap='circular' badgeContent='' variant='dot'>
      <PickersDay {...props} day={day} />
    </Badge>
  )
}

function ButtonField(props: ButtonFieldProps) {
  const {
    setOpen,
    label,
    id,
    disabled,
    InputProps: { ref } = {},
    inputProps: { 'aria-label': ariaLabel } = {}
  } = props

  return (
    <Button
      variant='outlined'
      id={id}
      disabled={disabled}
      ref={ref}
      aria-label={ariaLabel}
      size='large'
      onClick={() => setOpen?.((prev) => !prev)}
      startIcon={<CalendarTodayRoundedIcon fontSize='small' />}
      sx={{ minWidth: 'fit-content' }}
    >
      {label ? `${label}` : 'Pick a date'}
    </Button>
  )
}

export default function CustomDatePicker() {
  const [open, setOpen] = React.useState(false)
  const { protocolsList } = useProtocolsList()
  const [date, setDate] = useAtom(protocolsDate)
  const setLeaseDate = useSetAtom(leaseDate)
  const navigate = useNavigate()

  const onMonthChange = React.useCallback(
    (nextDate: Dayjs) => {
      setLeaseDate(getProtocolFetchRange(nextDate))
      setDate(nextDate.toISOString())
    },
    [setLeaseDate, setDate]
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='pl'>
      <DatePicker
        onYearChange={onMonthChange}
        onMonthChange={onMonthChange}
        onChange={(nextDate) => {
          if (nextDate) {
            setLeaseDate(getProtocolFetchRange(nextDate))
            setDate(nextDate.toISOString())
            navigate('/')
          }
        }}
        value={dayjs(date)}
        label={date == null ? null : dayjs(date).locale('pl').format('DD MMMM YYYY')}
        slots={{ field: ButtonField, day: Day }}
        slotProps={{
          field: { setOpen } as any,
          nextIconButton: { size: 'small' },
          previousIconButton: { size: 'small' },
          day: {
            protocols: protocolsList
          } as any
        }}
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        views={['day']}
      />
    </LocalizationProvider>
  )
}
